import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, type User } from './lib/api';
import { clearAlbumCache, purgeAlbumCacheExcept } from './lib/albumCache';
import { clearAlbumPhotoCache, purgeAlbumPhotoCacheExcept } from './lib/albumPhotoCache';
import { clearChatCache, purgeChatCacheExcept } from './lib/chatCache';
import { clearFeedCache, purgeFeedCacheExcept } from './lib/feedCache';
import {
  activatePhotoCacheForUser,
  clearPhotoCache,
  repairPhotoCacheWorkerBinding,
} from './lib/photoCache';
import { syncSubscription } from './lib/pushClient';

interface AuthState {
  user: User | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await api.me();
      if (result.authenticated && result.user) {
        await Promise.all([
          purgeAlbumCacheExcept(result.user.id),
          purgeAlbumPhotoCacheExcept(result.user.id),
          purgeChatCacheExcept(result.user.id),
          purgeFeedCacheExcept(result.user.id),
          activatePhotoCacheForUser(result.user.id),
        ]);
        setUser(result.user);
      } else {
        await Promise.all([clearAlbumCache(), clearAlbumPhotoCache(), clearChatCache(), clearFeedCache(), clearPhotoCache()]);
        setUser(null);
      }
    } catch {
      await Promise.all([clearAlbumCache(), clearAlbumPhotoCache(), clearChatCache(), clearFeedCache(), clearPhotoCache()]);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const invalidate = () => {
      void Promise.all([clearAlbumCache(), clearAlbumPhotoCache(), clearChatCache(), clearFeedCache(), clearPhotoCache()]).finally(() => setUser(null));
    };
    window.addEventListener('family:auth-invalidated', invalidate);
    return () => window.removeEventListener('family:auth-invalidated', invalidate);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.addEventListener('controllerchange', repairPhotoCacheWorkerBinding);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', repairPhotoCacheWorkerBinding);
  }, []);

  /**
   * Re-upload this device's push subscription whenever we confirm a signed-in
   * user. Subscriptions expire silently, so treating every launch as a chance
   * to repair the registration is what keeps notifications alive long-term.
   */
  useEffect(() => {
    if (user) void syncSubscription();
  }, [user]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Local privacy cleanup must still happen when the server is offline.
    } finally {
      await Promise.all([clearAlbumCache(), clearAlbumPhotoCache(), clearChatCache(), clearFeedCache(), clearPhotoCache()]);
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, refresh, signOut }),
    [user, loading, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
